import React, { useEffect, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import * as d3Scale from "d3-scale";
import { interpolatePurples } from "d3-scale-chromatic";
import { Tooltip } from "@mui/material";
import Colors from "./ui/Colors";

// State data
const STATES = [
  { code: "01", name: "Alabama" },
  { code: "02", name: "Alaska" },
  { code: "04", name: "Arizona" },
  { code: "05", name: "Arkansas" },
  { code: "06", name: "California" },
  { code: "08", name: "Colorado" },
  { code: "09", name: "Connecticut" },
  { code: "10", name: "Delaware" },
  { code: "11", name: "District of Columbia" },
  { code: "12", name: "Florida" },
  { code: "13", name: "Georgia" },
  { code: "15", name: "Hawaii" },
  { code: "16", name: "Idaho" },
  { code: "17", name: "Illinois" },
  { code: "18", name: "Indiana" },
  { code: "19", name: "Iowa" },
  { code: "20", name: "Kansas" },
  { code: "21", name: "Kentucky" },
  { code: "22", name: "Louisiana" },
  { code: "23", name: "Maine" },
  { code: "24", name: "Maryland" },
  { code: "25", name: "Massachusetts" },
  { code: "26", name: "Michigan" },
  { code: "27", name: "Minnesota" },
  { code: "28", name: "Mississippi" },
  { code: "29", name: "Missouri" },
  { code: "30", name: "Montana" },
  { code: "31", name: "Nebraska" },
  { code: "32", name: "Nevada" },
  { code: "33", name: "New Hampshire" },
  { code: "34", name: "New Jersey" },
  { code: "35", name: "New Mexico" },
  { code: "36", name: "New York" },
  { code: "37", name: "North Carolina" },
  { code: "38", name: "North Dakota" },
  { code: "39", name: "Ohio" },
  { code: "40", name: "Oklahoma" },
  { code: "41", name: "Oregon" },
  { code: "42", name: "Pennsylvania" },
  { code: "44", name: "Rhode Island" },
  { code: "45", name: "South Carolina" },
  { code: "46", name: "South Dakota" },
  { code: "47", name: "Tennessee" },
  { code: "48", name: "Texas" },
  { code: "49", name: "Utah" },
  { code: "50", name: "Vermont" },
  { code: "51", name: "Virginia" },
  { code: "53", name: "Washington" },
  { code: "54", name: "West Virginia" },
  { code: "55", name: "Wisconsin" },
  { code: "56", name: "Wyoming" }
];

// State areas (square miles)
const STATE_AREAS = {
  "01": 50645,  "02": 663267, "04": 113990, "05": 52035,  "06": 155779,
  "08": 103642, "09": 4842,   "10": 1954,   "11": 68,     "12": 53927,
  "13": 57513,  "15": 6423,   "16": 82643,  "17": 55519,  "18": 35826,
  "19": 55857,  "20": 81759,  "21": 39486,  "22": 43204,  "23": 30843,
  "24": 9707,   "25": 7840,   "26": 56539,  "27": 79627,  "28": 46923,
  "29": 68742,  "30": 145546, "31": 76824,  "32": 109781, "33": 8953,
  "34": 7354,   "35": 121298, "36": 47214,  "37": 48618,  "38": 68986,
  "39": 40861,  "40": 68595,  "41": 95988,  "42": 44743,  "44": 1034,
  "45": 30061,  "46": 75811,  "47": 41235,  "48": 261232, "49": 82170,
  "50": 9217,   "51": 39490,  "53": 66456,  "54": 24038,  "55": 54158,
  "56": 97093
};

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const USMap = ({ data, selectedState, onStateSelect }) => {
  const [stateData, setStateData] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    console.log("USMap received data:", data);
    
    if (!data || data.length === 0) {
      console.log("No data or empty data array received");
      setStateData([]);
      return;
    }

    
    const processedData = data.map(item => {
      // Ensure we have a state code
      if (!item.state && !item.stateCode) {
        //console.log("Item missing state code:", item);
        return null;
      }
      
      // Use either state or stateCode property depending on API response format
      const stateCode = item.state || item.stateCode;
      
      // Use either value or population property depending on API response format
      const populationValue = Number(item.population || item.value || 0);
      
      return {
        state: stateCode,
        value: populationValue,
        // Keep the sanity check if needed
        sanityCheckedValue: populationValue > 1000000000 ? populationValue / 1000 : populationValue
      };
    }).filter(item => item !== null); // Remove any null items
    
    console.log("Simplified processed data:", processedData);
    setStateData(processedData);
  }, [data]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Find min and max population for color scale
  const values = stateData.map(d => d.value);
  const minPop = values.length > 0 ? Math.min(...values) : 0;
  const maxPop = values.length > 0 ? Math.max(...values) : 1;
  
  console.log("Population range:", minPop, "to", maxPop);
  
  // Use a power scale to enhance low population visibility
  const colorScale = d3Scale.scalePow()
    .exponent(0.5) // Square root scale makes low values more visible
    .domain([0, maxPop])
    .range([0.35, 1]); // Start from 0.3 instead of 0 to avoid very light colors
  
  // Use Purples color scheme
  const colorInterpolator = interpolatePurples;

  // Helper function to format population numbers in a readable way
  const formatPopulation = (value) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(0) + 'K';
    } else {
      return value.toFixed(0);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 800 }}
        height={500}
        width={800}
      >
        <ZoomableGroup zoom={1}>
          <Geographies geography={geoUrl}>
            {({ geographies }) => (
              geographies && geographies.map(geo => {
                const stateCode = String(geo.id).padStart(2, '0');
                const stateDataItem = stateData.find(d => d.state === stateCode);
                
                const population = stateDataItem?.value || 0;
                
                // Determine if this is the selected state
                const isSelected = selectedState === stateCode;
                // Use Colors.third for selected state, otherwise use population-based color
                const normalizedValue = colorScale(population);
                const color = isSelected 
                  ? Colors.third  // Highlight color for selected state
                  : (population > 0 
                      ? colorInterpolator(normalizedValue) 
                      : "#e8e1f2");
                
                return (
                  <Tooltip
                    key={geo.rsmKey}
                    title={
                      <>
                        <strong>{STATES.find(s => s.code === stateCode)?.name}</strong>
                        <div>Population: {population.toLocaleString()}</div>
                      </>
                    }
                    arrow
                  >
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={color}
                      stroke="#FFF"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          fill: Colors.third,
                          cursor: "pointer",
                          outline: "none"
                        },
                        pressed: { outline: "none" }
                      }}
                      onClick={() => onStateSelect(stateCode)}
                    />
                  </Tooltip>
                );
              })
            )}
          </Geographies>
          
          {/* Selected State Highlight */}
          {selectedState && selectedState !== "all" && (
            <Geographies geography={geoUrl}>
              {({ geographies }) => geographies
                .filter(geo => String(geo.id).padStart(2, '0') === selectedState)
                .map(geo => (
                  <Geography
                    key={geo.rsmKey + "-highlight"}
                    geography={geo}
                    fill="none"
                    stroke={Colors.primary}
                    strokeWidth={2}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" }
                    }}
                  />
                ))}
            </Geographies>
          )}
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      <div style={{
        position: 'absolute',
        ...(windowWidth < 768 
          ? { bottom: -40, right: 10 } // Bottom right on mobile
          : { bottom: 10, left: 20 }), // Bottom left on desktop
        backgroundColor: 'white',
        padding: windowWidth < 768 ? '8px' : '12px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        fontSize: windowWidth < 768 ? '90%' : '100%',
        maxWidth: windowWidth < 768 ? '140px' : '180px',
        zIndex: 1000 // Ensure it's above other elements
      }}>
        <div style={{ 
          marginBottom: windowWidth < 768 ? '4px' : '8px', 
          fontWeight: 'bold', 
          fontSize: windowWidth < 768 ? '12px' : '14px' 
        }}>
          Population by State
        </div>
        
        {/* Color gradient with labels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: windowWidth < 768 ? '2px' : '4px' }}>
          {/* Create 5 rows from high to low for better readability */}
          {[0.9, 0.7, 0.5, 0.4, 0.3].map((t, i) => {
            // Calculate population thresholds at nice round numbers
            let popValue;
            if (i === 0) popValue = maxPop;
            else if (i === 1) popValue = Math.round(maxPop * 0.75 / 1000000) * 1000000;
            else if (i === 2) popValue = Math.round(maxPop * 0.5 / 1000000) * 1000000;
            else if (i === 3) popValue = Math.round(maxPop * 0.25 / 1000000) * 1000000;
            else popValue = 0;
            
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: windowWidth < 768 ? '15px' : '20px',
                  height: windowWidth < 768 ? '15px' : '20px',
                  backgroundColor: colorInterpolator(t),
                  marginRight: windowWidth < 768 ? '4px' : '8px',
                  border: '1px solid #ddd'
                }} />
                <span style={{ fontSize: windowWidth < 768 ? '11px' : '13px' }}>
                  {i === 0 ? `${formatPopulation(popValue)}+` : 
                   i === 4 ? `< ${formatPopulation(Math.round(maxPop * 0.25 / 1000000) * 1000000)}` :
                   `${formatPopulation(popValue)}`}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Only show this on larger screens */}
        {windowWidth >= 768 && (
          <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
            {new Date().getFullYear()} population data
          </div>
        )}
      </div>
    </div>
  );
};

export default USMap; 